import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../api/axios';
import { fmtTime } from '../../utils/dateFormat';

const BRAND = '#0090bb';

// Miqdarı təmiz göstər: 1.000 → 1, 1.500 → 1.5
const fmtQ = (q) => String(Math.round((Number(q) || 0) * 1000) / 1000);

const statusLabel = {
    gozleyir: 'Gözləyir',
    hazirlanir: 'Hazırlanır',
    hazir: 'Hazır'
};

const statusClass = {
    gozleyir: 'bg-amber-100 text-amber-700',
    hazirlanir: 'bg-sky-100 text-sky-700',
    hazir: 'bg-emerald-100 text-emerald-700'
};

const KitchenDisplay = () => {
    const [searchParams] = useSearchParams();
    // Şifrəsiz token: "<kitchen_token>" və ya "<kitchen_token>_<printer>". İlk "_" ayırır.
    const rawT = String(searchParams.get('t') || '').trim();
    const _us = rawT.indexOf('_');
    const token = _us >= 0 ? rawT.slice(0, _us) : rawT;              // yalnız token (validasiya üçün)
    const printerFromToken = _us >= 0 ? rawT.slice(_us + 1) : '';
    // Printer filtri: tokendəki suffiks, yoxsa köhnə ?printer=/&p= parametri
    const printerFilter = String(printerFromToken || searchParams.get('printer') || searchParams.get('p') || '').trim().toLowerCase();
    const matchPrinter = (it) => !printerFilter || String(it?.printer || '').toLowerCase().includes(printerFilter);

    const [tenant, setTenant] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [time, setTime] = useState(new Date());
    const [updatingItemId, setUpdatingItemId] = useState(null);
    const [fetchedAt, setFetchedAt] = useState(Date.now()); // sifarişlərin çəkildiyi an (canlı keçən vaxt üçün)

    // ── Səs bildirişi (yeni sifariş/məhsul gələndə) + mute düyməsi ──
    const [muted, setMuted] = useState(() => { try { return localStorage.getItem('pos_kitchen_muted') === '1'; } catch { return false; } });
    const mutedRef = useRef(muted);
    useEffect(() => { mutedRef.current = muted; }, [muted]);
    const audioCtxRef = useRef(null);
    const prevItemIdsRef = useRef(null); // əvvəlki çəkilişin məhsul ID-ləri (null = ilk yükləmə)

    // AudioContext-i hazırla/oyat (avtoplay siyasəti üçün istifadəçi jestindən sonra işə düşür)
    const ensureAudio = () => {
        try {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) return null;
            if (!audioCtxRef.current) audioCtxRef.current = new AC();
            if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
            return audioCtxRef.current;
        } catch { return null; }
    };

    // İki tonlu "ding-dong" bip
    const playBeep = () => {
        const ctx = ensureAudio();
        if (!ctx) return;
        const now = ctx.currentTime;
        [[880, 0], [1175, 0.18]].forEach(([freq, offset]) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;
            const t = now + offset;
            gain.gain.setValueAtTime(0.0001, t);
            gain.gain.exponentialRampToValueAtTime(0.35, t + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
            osc.connect(gain); gain.connect(ctx.destination);
            osc.start(t); osc.stop(t + 0.4);
        });
    };

    // İlk istifadəçi jestində audio-nu oyat (TV-də bir dəfə toxunmaq kifayətdir)
    useEffect(() => {
        const unlock = () => ensureAudio();
        window.addEventListener('pointerdown', unlock, { once: true });
        window.addEventListener('keydown', unlock, { once: true });
        return () => {
            window.removeEventListener('pointerdown', unlock);
            window.removeEventListener('keydown', unlock);
        };
    }, []);

    const toggleMute = () => {
        setMuted((m) => {
            const next = !m;
            try { localStorage.setItem('pos_kitchen_muted', next ? '1' : '0'); } catch { /* ignore */ }
            if (!next) { ensureAudio(); playBeep(); } // səsi açanda test bip + audio oyat
            return next;
        });
    };
    // Sifariş açılandan keçən vaxt (canlı): backend elapsed_sec + çəkilişdən bəri keçən
    const fmtElapsed = (order) => {
        const sec = Math.max(0, Math.floor(Number(order.elapsed_sec || 0) + (time.getTime() - fetchedAt) / 1000));
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return m > 0 ? `${m} dəq ${s} san` : `${s} san`;
    };
    // Masa kartı: default YIĞILI (collapsed[id] === false → açıq, başqa hər şey → yığılı)
    const [collapsed, setCollapsed] = useState({});
    const isCollapsed = (id) => collapsed[id] !== false;
    const toggleCollapse = (id) => setCollapsed((c) => ({ ...c, [id]: (c[id] !== false) ? false : true }));

    useEffect(() => {
        const interval = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        let active = true;

        const loadOrders = async (silent = false) => {
            if (!token) {
                if (active) {
                    setError('Mətbəx ekranı linki yanlışdır.');
                    setLoading(false);
                }
                return;
            }

            try {
                // Şifrəsiz (public) endpoint — login tələb etmir; tam `t`-ni göndər (backend token-i parse edir)
                const res = await api.get(`/orders/kitchen-screen?t=${encodeURIComponent(rawT)}`);
                if (!active) return;
                setTenant(res.data?.tenant || null);
                const nextOrders = res.data?.data || [];
                setOrders(nextOrders);
                setFetchedAt(Date.now());
                setError('');

                // Yeni məhsul (yeni sifariş və ya mövcud sifarişə əlavə) → səs bildirişi.
                // Printer filtrinə uyğun məhsulları nəzərə al (soyuq ekran yalnız soyuq üçün səs versin).
                const curIds = [];
                nextOrders.forEach((o) => (o.items || []).forEach((it) => { if (matchPrinter(it)) curIds.push(it.id); }));
                const prev = prevItemIdsRef.current;
                if (prev !== null) {
                    const hasNew = curIds.some((id) => !prev.has(id));
                    if (hasNew && !mutedRef.current) playBeep();
                }
                prevItemIdsRef.current = new Set(curIds);
            } catch (err) {
                if (!active) return;
                // Giriş yoxdursa / icazə yoxdursa → login səhifəsinə yönləndir (linki olan hamı girə bilməsin)
                const st = err.response?.status;
                if (st === 401 || st === 403) {
                    window.location.replace(`/?t=${encodeURIComponent(token)}&login=staff`);
                    return;
                }
                if (!silent) {
                    setError(err.response?.data?.msg || 'Mətbəx ekranı yüklənmədi.');
                }
            } finally {
                if (active && !silent) {
                    setLoading(false);
                }
            }
        };

        loadOrders();
        const refresh = setInterval(() => loadOrders(true), 10000);
        return () => {
            active = false;
            clearInterval(refresh);
        };
    }, [token]);

    // Çıxış: tokeni saxla, sessiyanı təmizlə, həmin restoranın login səhifəsinə qayıt
    const handleLogout = () => {
        if (token) localStorage.setItem('pos_waiter_token', token);
        localStorage.removeItem('pos_token');
        localStorage.removeItem('pos_user');
        // login=staff → birbaşa Admin/İşçi/Mətbəx (şifrə) tab-ı açılsın
        window.location.replace(`/?t=${encodeURIComponent(token)}&login=staff`);
    };

    const reloadOrders = async () => {
        if (!token) return;
        const res = await api.get(`/orders/kitchen-screen?t=${encodeURIComponent(rawT)}`);
        setTenant(res.data?.tenant || null);
        setOrders(res.data?.data || []);
        setFetchedAt(Date.now());
    };

    const updateItemStatus = async (itemId, status) => {
        setUpdatingItemId(itemId);
        try {
            await api.put(`/orders/kitchen-screen-item/${itemId}`, { t: rawT, status });
            await reloadOrders();
        } catch (err) {
            setError(err.response?.data?.msg || 'Status yenilənmədi.');
        } finally {
            setUpdatingItemId(null);
        }
    };

    // Printer filtrinə uyğun məhsulları saxla; uyğun məhsulu olmayan sifarişləri gizlət
    const visibleOrders = orders
        .map((o) => ({ ...o, items: (o.items || []).filter(matchPrinter) }))
        .filter((o) => o.items.length > 0)
        .sort((a, b) => (b.is_today ? 1 : 0) - (a.is_today ? 1 : 0)); // bugünkülər öndə, köhnələr sonda

    // Görünüş növü: 'v2' = TV rejimi (kiçik, hamısı açıq, düyməsiz, max yerləşən)
    const mode = tenant?.display_mode === 'v2' ? 'v2' : 'v1';

    return (
        <div className="min-h-screen bg-[radial-gradient(circle_at_top,#dff6fd_0%,#edf8fc_45%,#f6fbfd_100%)] text-gray-900">
            <div className={`mx-auto px-3 py-4 sm:px-4 sm:py-5 ${mode === 'v2' ? 'max-w-none' : 'max-w-7xl'}`}>
                <div className="mb-4 overflow-hidden rounded-[28px] border border-[#cbeaf4] bg-white shadow-[0_20px_60px_rgba(0,144,187,0.12)]">
                    <div className="bg-[linear-gradient(135deg,#0090bb,#00a9d7)] px-4 py-4 text-white sm:px-5 sm:py-5">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                                <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/80 sm:text-[11px]">Mətbəx ekranı</div>
                                <h1 className="mt-0.5 truncate text-xl font-black sm:text-2xl">{tenant?.name || 'Mətbəx ekranı'}</h1>
                                {printerFilter && (
                                    <div className="mt-1 inline-flex rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider">
                                        🖨️ {printerFilter}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center justify-between gap-2 sm:justify-end">
                                <div className="rounded-xl bg-white/15 px-3 py-2 text-center backdrop-blur sm:text-right">
                                    <div className="text-[10px] text-white/80">Canlı</div>
                                    <div className="text-lg font-black leading-tight tabular-nums sm:text-xl">
                                        {`${fmtTime(time)}:${String(time.getSeconds()).padStart(2, '0')}`}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={toggleMute}
                                    className="shrink-0 rounded-xl bg-white/15 px-3 py-2 text-sm font-bold text-white backdrop-blur transition-all hover:bg-white/25"
                                    title={muted ? 'Səsi aç' : 'Səsi bağla'}
                                >
                                    {muted ? '🔇 Səs bağlı' : '🔔 Səs açıq'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleLogout}
                                    className="shrink-0 rounded-xl bg-white/15 px-3 py-2 text-sm font-bold text-white backdrop-blur transition-all hover:bg-white/25"
                                    title="Çıxış"
                                >
                                    🚪 Çıxış
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="px-4 py-4 text-sm text-[#376473] sm:px-5">
                        Açıq sifarişlər 10 saniyədən bir yenilənir. Hazırlanan məhsullar rəngli status ilə görünür.
                    </div>
                </div>

                {loading ? (
                    <div className="rounded-[28px] bg-white p-10 text-center text-gray-400 shadow-sm">Yüklənir...</div>
                ) : error ? (
                    <div className="rounded-[28px] bg-white p-10 text-center text-red-500 shadow-sm">{error}</div>
                ) : visibleOrders.length === 0 ? (
                    <div className="rounded-[28px] bg-white p-10 text-center text-gray-400 shadow-sm">
                        Hazırda gözləyən mətbəx sifarişi yoxdur.
                    </div>
                ) : mode === 'v2' ? (
                    /* ===== V2 — TV rejimi: kiçik kartlar, hamısı açıq, düymə yoxdur ===== */
                    <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                        {visibleOrders.map((order) => (
                            <div
                                key={order.id}
                                className={`flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm ${order.is_today ? 'border-[#d2ebf3]' : 'border-amber-200 opacity-60 grayscale'}`}
                            >
                                {order.daily_number ? (
                                    <div className="bg-orange-500 py-1 text-center">
                                        <div className="text-5xl font-black leading-none text-white tabular-nums">{order.daily_number}</div>
                                    </div>
                                ) : null}
                                <div className="flex items-baseline justify-between gap-2 border-b border-[#e4f2f7] bg-[#f6fbfd] px-2.5 py-1.5">
                                    <div className="min-w-0">
                                        <span className="text-base font-black text-gray-900">{order.table_name || `Masa ${order.table_number}`}</span>
                                        {!order.is_today && <span className="ml-1 text-[10px] font-bold text-amber-600">🕓 köhnə</span>}
                                    </div>
                                    <div className="shrink-0 text-right leading-tight">
                                        <div className="text-[10px] text-gray-400">#{order.id}</div>
                                        <div className="text-xs font-black text-red-600 tabular-nums">⏱ {fmtElapsed(order)}</div>
                                    </div>
                                </div>
                                <div className="flex-1 divide-y divide-[#eef5f8]">
                                    {(order.items || []).map((item) => (
                                        <div
                                            key={item.id}
                                            className={`px-2.5 py-1.5 ${item.status === 'hazir' ? 'bg-emerald-50' : item.status === 'hazirlanir' ? 'bg-sky-50' : ''}`}
                                        >
                                            <div className="flex items-baseline justify-between gap-2">
                                                <span className="text-[15px] font-bold leading-tight text-gray-900">{item.item_name}</span>
                                                <span className="shrink-0 text-[15px] font-black tabular-nums" style={{ color: BRAND }}>×{fmtQ(item.qty)}</span>
                                            </div>
                                            {item.set_name && <div className="text-[11px] font-semibold text-purple-600">🎁 {item.set_name}</div>}
                                            {item.recipe && item.recipe.length > 0 && (
                                                <div className="mt-0.5 text-[11px] leading-tight text-gray-500">{item.recipe.map((l, i) => <div key={i}>• {l}</div>)}</div>
                                            )}
                                            {item.note && <div className="mt-0.5 text-[11px] font-semibold text-amber-700">✎ {item.note}</div>}
                                        </div>
                                    ))}
                                </div>
                                {order.note && (
                                    <div className="bg-[#0090bb] px-2.5 py-1 text-[11px] font-medium text-white">📝 {order.note}</div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
                        {visibleOrders.map((order) => (
                            <div
                                key={order.id}
                                className={`overflow-hidden rounded-[28px] border bg-white shadow-[0_18px_45px_rgba(0,144,187,0.08)] ${order.is_today ? 'border-[#d2ebf3]' : 'border-amber-200 opacity-60 grayscale'}`}
                            >
                                <div className="border-b border-[#e4f2f7] bg-[#f6fbfd] px-4 py-4">
                                    {/* Fast food: gündəlik sıra nömrəsi böyük başda */}
                                    {order.daily_number ? (
                                        <div className="mb-3 rounded-2xl bg-orange-500 px-4 py-3 text-center">
                                            <div className="text-[7rem] font-black leading-none text-white tabular-nums">{order.daily_number}</div>
                                        </div>
                                    ) : null}
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: BRAND }}>
                                                Masa {order.table_number}
                                            </div>
                                            <div className="mt-1 truncate text-2xl font-black text-gray-900">{order.table_name}</div>
                                            {!order.is_today && (
                                                <span className="mt-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700">🕓 Əvvəlki gündən açıq</span>
                                            )}
                                            <div className="mt-1 text-sm text-gray-500">Ofisiant: {order.waiter_name}</div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="rounded-2xl bg-white px-3 py-2 text-right text-xs text-gray-500 shadow-sm">
                                                <div>Sifariş #{order.id}</div>
                                                <div className="mt-1 font-semibold text-gray-700">
                                                    {fmtTime(order.opened_at)}
                                                </div>
                                                <div className="mt-1 font-black text-red-600 tabular-nums">⏱ {fmtElapsed(order)}</div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => toggleCollapse(order.id)}
                                                title={isCollapsed(order.id) ? 'Aç' : 'Yığ'}
                                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-lg font-black text-[#0090bb] shadow-sm hover:bg-[#eef7fa]"
                                            >
                                                {isCollapsed(order.id) ? '▸' : '▾'}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {isCollapsed(order.id) ? (
                                    <button
                                        type="button"
                                        onClick={() => toggleCollapse(order.id)}
                                        className="w-full px-4 py-2.5 text-center text-xs font-semibold text-[#0090bb] hover:bg-[#f6fbfd]"
                                    >
                                        {(order.items || []).length} məhsul yığılıb — açmaq üçün toxun
                                    </button>
                                ) : (
                                <>
                                <div className="space-y-3 px-4 py-4">
                                    {(order.items || []).map((item) => (
                                        <div
                                            key={item.id}
                                            className="rounded-[22px] border border-[#e1eef3] bg-[linear-gradient(180deg,#ffffff_0%,#f9fcfd_100%)] px-4 py-3"
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="truncate text-lg font-black text-gray-900">{item.item_name}</div>
                                                    <div className="mt-1 text-sm text-gray-500">Miqdar: {fmtQ(item.qty)}</div>
                                                </div>
                                                <span className={`rounded-full px-3 py-1 text-xs font-bold ${statusClass[item.status] || 'bg-gray-100 text-gray-600'}`}>
                                                    {statusLabel[item.status] || item.status}
                                                </span>
                                            </div>
                                            {item.set_name && (
                                                <div className="mt-2 inline-flex items-center gap-1 rounded-md bg-purple-100 px-2 py-0.5 text-[11px] font-bold text-purple-700">
                                                    🎁 Set: {item.set_name}
                                                </div>
                                            )}
                                            {item.recipe && item.recipe.length > 0 && (
                                                <div className="mt-2 rounded-xl bg-[#eef5f8] px-3 py-2">
                                                    <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[#5b7c89]">📋 Texkarta</div>
                                                    {item.recipe.map((line, idx) => (
                                                        <div key={idx} className="text-[15px] leading-relaxed text-gray-700">• {line}</div>
                                                    ))}
                                                </div>
                                            )}
                                            {item.note && (
                                                <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                                    Qeyd: {item.note}
                                                </div>
                                            )}
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {[
                                                    { key: 'gozleyir', label: 'Gözləyir' },
                                                    { key: 'hazirlanir', label: 'Hazırlanır' },
                                                    { key: 'hazir', label: 'Hazır' }
                                                ].map((option) => (
                                                    <button
                                                        key={option.key}
                                                        type="button"
                                                        onClick={() => updateItemStatus(item.id, option.key)}
                                                        disabled={updatingItemId === item.id || item.status === option.key}
                                                        className={`rounded-full px-3 py-2 text-xs font-bold transition-all ${
                                                            item.status === option.key
                                                                ? 'text-white'
                                                                : 'border border-[#d7e5eb] bg-white text-[#0090bb]'
                                                        } disabled:cursor-not-allowed disabled:opacity-60`}
                                                        style={item.status === option.key ? { backgroundColor: BRAND } : {}}
                                                    >
                                                        {updatingItemId === item.id && item.status !== option.key ? 'Yenilənir...' : option.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {order.note && (
                                    <div className="border-t border-[#e4f2f7] bg-[linear-gradient(135deg,#0090bb,#00a8d4)] px-4 py-3 text-sm font-medium text-white">
                                        Sifariş qeydi: {order.note}
                                    </div>
                                )}
                                </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                <div className="mt-6 pb-4 text-center text-xs text-gray-500">
                    Powered by{' '}
                    <a
                        href="https://www.fiscalbusiness.net"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-bold underline underline-offset-4"
                        style={{ color: BRAND }}
                    >
                        www.fiscalbusiness.net
                    </a>
                </div>
            </div>
        </div>
    );
};

export default KitchenDisplay;
